import { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api, reelThumbnail } from "../lib/api";
import { Printer } from "@phosphor-icons/react";

/**
 * Printable "Live Feed" QR poster for trade-show booths.
 *
 * A single 16:9 or 3:4 sheet with:
 *   • a big QR pointing to /live
 *   • an auto-rotating carousel of reel thumbnails (5s per reel)
 *   • booth-ready branding + a scannable CTA
 *
 * Screen params:
 *   /live/poster?url=https://wisewit.ai/live      (override the QR target)
 *   /live/poster?orient=portrait                  (default is landscape)
 *
 * Print CSS shrinks the header/print button and forces a full-page A4.
 */
export default function LivePoster() {
  const [reels, setReels] = useState([]);
  const [idx, setIdx] = useState(0);
  const params = new URLSearchParams(window.location.search);
  const qrUrl = params.get("url") || `${window.location.origin}/live`;
  const orient = params.get("orient") === "portrait" ? "portrait" : "landscape";

  useEffect(() => {
    api.get("/reels").then(({ data }) => setReels(data)).catch(() => {});
    document.title = "Wisewit — Live Feed Poster";
  }, []);

  useEffect(() => {
    if (!reels.length) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % reels.length), 5000);
    return () => clearInterval(t);
  }, [reels.length]);

  const current = reels[idx];
  const thumb = useMemo(() => (current ? reelThumbnail(current) : ""), [current]);

  return (
    <div data-testid="live-poster" className={`min-h-screen bg-[#050505] text-white ${orient === "portrait" ? "poster-portrait" : "poster-landscape"}`}>
      <style>{`
        @media print {
          @page { size: ${orient === "portrait" ? "A4 portrait" : "A4 landscape"}; margin: 0; }
          body { background: #050505; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .poster-sheet { min-height: 100vh; }
        }
      `}</style>

      <div className="no-print px-6 py-4 flex items-center justify-between border-b border-[#2A2A2A] bg-[#0a0a0a]">
        <div>
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#DFFF00]">◢ Live Feed Poster</div>
          <div className="text-[#8E8E93] text-sm mt-1">Preview below — hit print for an A4 handout.</div>
        </div>
        <div className="flex items-center gap-3">
          <a href="/live/poster?orient=portrait" className={`text-[10px] font-mono tracking-[0.18em] uppercase border px-3 py-2 ${orient === "portrait" ? "border-[#DFFF00] text-[#DFFF00]" : "border-[#2A2A2A] text-[#8E8E93] hover:text-white"}`} data-testid="poster-portrait-toggle">Portrait</a>
          <a href="/live/poster" className={`text-[10px] font-mono tracking-[0.18em] uppercase border px-3 py-2 ${orient === "landscape" ? "border-[#DFFF00] text-[#DFFF00]" : "border-[#2A2A2A] text-[#8E8E93] hover:text-white"}`} data-testid="poster-landscape-toggle">Landscape</a>
          <button onClick={() => window.print()} className="btn-primary" data-testid="poster-print"><Printer size={14} /> Print</button>
        </div>
      </div>

      <div className={`poster-sheet mx-auto bg-[#050505] relative overflow-hidden ${orient === "portrait" ? "max-w-2xl aspect-[1/1.414]" : "max-w-6xl aspect-[1.414/1]"} grid grid-cols-2 gap-8 p-12 md:p-16`}>
        {/* Left column: identity + carousel */}
        <div className="flex flex-col justify-between">
          <div>
            <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-[#DFFF00]">◢ WISEWIT · Live Feed</div>
            <h1 className={`mt-6 font-display text-white leading-[0.98] tracking-tighter ${orient === "portrait" ? "text-5xl" : "text-6xl"}`}>
              AI you can watch<br/>working <span className="text-[#FF4500]">live.</span>
            </h1>
            <p className="mt-6 text-[#8E8E93] text-lg leading-relaxed max-w-md">
              Every clip is a real system we shipped for a real customer. Scan → scroll → shortlist which one solves your line.
            </p>
          </div>

          {current && (
            <div data-testid="poster-carousel" className="mt-8 border border-[#2A2A2A] overflow-hidden">
              <div className="aspect-[16/9] bg-[#0a0a0a] relative">
                {thumb ? (
                  <img src={thumb} alt={current.title} className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-[#555] font-mono text-xs">no thumbnail</div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-[#DFFF00]">{current.category}</div>
                  <div className="mt-1 font-display text-lg text-white line-clamp-2">{current.title}</div>
                </div>
              </div>
              <div className="px-3 py-2 border-t border-[#2A2A2A] flex items-center justify-between">
                <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-[#8E8E93]">reel {idx + 1} / {reels.length}</div>
                <div className="flex items-center gap-1">
                  {reels.slice(0, 8).map((_, i) => (
                    <div key={i} className={`h-1 w-4 ${i === idx ? "bg-[#DFFF00]" : "bg-[#2A2A2A]"}`} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right column: QR + booth CTA */}
        <div className="flex flex-col items-center justify-center gap-8">
          <div className="p-6 bg-white">
            <QRCodeSVG value={qrUrl} size={orient === "portrait" ? 260 : 300} level="M" bgColor="#FFFFFF" fgColor="#050505" data-testid="poster-qr" />
          </div>
          <div className="text-center">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#DFFF00]">◢ Scan to watch the reel feed</div>
            <div className="mt-3 font-mono text-lg text-white break-all">{qrUrl.replace(/^https?:\/\//, "")}</div>
          </div>
          <div className="mt-2 border-t border-[#2A2A2A] pt-6 w-full text-center">
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#8E8E93]">Talk to us at the booth</div>
            <div className="mt-2 font-display text-2xl text-white">Wisewit AI · Booth</div>
            <div className="mt-1 font-mono text-xs text-[#8E8E93]">hello@wisewit.ai · wisewit.ai</div>
          </div>
        </div>
      </div>
    </div>
  );
}
